import { apiClient } from './apiClient';

/**
 * Calendar Service - Fetch and manage calendar events
 */
export class CalendarService {
    constructor() {
        this.BASE_URL = '/calendar-events';
    }

    /**
     * Get calendar events for a date range
     */
    async getCalendarEvents(startDate, endDate, filters = {}) {
        try {
            const params = new URLSearchParams({
                startDate,
                endDate,
                ...filters
            });
            const response = await apiClient.get(`${this.BASE_URL}?${params}`);
            return response;
        } catch (error) {
            console.error('Error fetching calendar events:', error);
            throw error;
        }
    }

    /**
     * Get events for a specific day
     */
    async getEventsForDay(date, filters = {}) {
        try {
            const response = await apiClient.get(`${this.BASE_URL}/day/${date}`, { params: filters });
            return response;
        } catch (error) {
            console.error('Error fetching day events:', error);
            throw error;
        }
    }

    /**
     * Get event details
     */
    async getEventDetails(eventId) {
        try {
            const response = await apiClient.get(`${this.BASE_URL}/${eventId}`);
            return response;
        } catch (error) {
            console.error('Error fetching event details:', error);
            throw error;
        }
    }

    /**
     * Get events by type for a user
     */
    async getEventsByType(userId, eventType, startDate, endDate) {
        try {
            const response = await apiClient.get(`${this.BASE_URL}/by-type/${eventType}`, {
                params: { userId, startDate, endDate }
            });
            return response;
        } catch (error) {
            console.error('Error fetching events by type:', error);
            throw error;
        }
    }

    /**
     * 🆕 Create a new event
     */
    async createEvent(eventData) {
        try {
            const response = await apiClient.post(this.BASE_URL, eventData);
            return response;
        } catch (error) {
            console.error('Error creating event:', error);
            throw error;
        }
    }

    /**
     * Update event
     */
    async updateEvent(eventId, updatedData) {
        try {
            const response = await apiClient.put(`${this.BASE_URL}/${eventId}`, updatedData);
            return response;
        } catch (error) {
            console.error('Error updating event:', error);
            throw error;
        }
    }

    /**
     * 🆕 Update event status only
     * @param {string} eventId
     * @param {'pending' | 'completed' | 'cancelled'} updatedStatus
     */
    async updateEventStatus(eventId, updatedStatus) {
        try {
            if (!['pending', 'completed', 'cancelled'].includes(updatedStatus)) {
                throw new Error(`Invalid status: ${updatedStatus}`);
            }

            const response = await apiClient.patch(`${this.BASE_URL}/${eventId}/status`, { status: updatedStatus });
            return response;
        } catch (error) {
            console.error('Error updating event status:', error);
            throw error;
        }
    }

    /**
     * Delete event
     */
    async deleteEvent(eventId) {
        try {
            const response = await apiClient.delete(`${this.BASE_URL}/${eventId}`);
            return response;
        } catch (error) {
            console.error('Error deleting event:', error);
            throw error;
        }
    }
}

export const calendarService = new CalendarService();
export default calendarService;
